import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box,
  Card,
  Menu,
  Table,
  Stack,
  Button,
  Tooltip,
  Divider,
  Checkbox,
  MenuItem,
  TextField,
  TableBody,
  Container,
  IconButton,
  ListItemText,
  TableContainer,
  TablePagination,
} from '@mui/material';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';

// hooks
import useAuth from '../../../hooks/useAuth';
import useTabs from '../../../hooks/useTabs';
import useSettings from '../../../hooks/useSettings';
import useTable, { getComparator, emptyRows } from '../../../hooks/useTable';
import useColumnVisibility, { ColumnDef } from '../../../hooks/useColumnVisibility';
// _mock_
// components
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import {
  TableNoData,
  ColumnsButton,
  TableEmptyRows,
  TableHeadCustom,
  TableSelectedActions,
} from '../../../components/table';
// sections
import { UserTableToolbar } from '../../@dashboard/user/list';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { PromoModal } from './PromoModal';
import { PromoCodeType, PROMO_SOURCES } from 'src/@types/PromoCode';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import PromoCodeTableRow from 'src/sections/@dashboard/PromoCode/PromoTableRow';

// ----------------------------------------------------------------------

const Sort_Options = ['all', 'acsending', 'descending'];

// Customizable filters (same pattern as the Students list): the admin chooses
// which filters show; the choice persists locally. Default = Source + Status.
const PROMO_FILTER_DEFS: { key: string; label: string }[] = [
  { key: 'source', label: 'Source' },
  { key: 'status', label: 'Status' },
  { key: 'partner', label: 'Partner name' },
];
const PROMO_FILTERS_STORAGE_KEY = 'kayfa.promo.visibleFilters';
const PROMO_DEFAULT_VISIBLE = ['source', 'status'];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Any status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'expired', label: 'Expired' },
];

// Toggleable promo columns (checkbox + actions are fixed). Ids match the cells
// gated in PromoTableRow.
const PROMO_COLUMNS: ColumnDef[] = [
  { id: 'Code', label: 'Code', align: 'left' },
  { id: 'amount', label: 'Percentage', align: 'left' },
  { id: 'usageLimit', label: 'Usage Limit', align: 'left' },
  { id: 'usageCount', label: 'Usage Count', align: 'left' },
  { id: 'source', label: 'Source', align: 'left' },
  { id: 'scope', label: 'Scope', align: 'left' },
  { id: 'status', label: 'Status', align: 'left' },
];
// Bumped to .v2 so the new Scope column shows for admins with a saved set.
const PROMO_COLUMNS_STORAGE_KEY = 'kayfa.promo.visibleColumns.v2';

// ----------------------------------------------------------------------

export default function PromoList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  // Deleting a promo code is super-admin-only (mirrors the BE @Auth on DELETE).
  const isSuperAdmin = (((user as any)?.roles as string[]) ?? []).includes('super-admin');
  const [deleting, setDeleting] = useState(false);


  const [tableData, setTableData] = useState<PromoCodeType[]>([]);
  const [filterRole, setFilterRole] = useState('all');
  const [filterName, setFilterName] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPromoCode, setCurrentPromoCode] = useState<PromoCodeType | undefined>();

  // Customizable filters — which controls are shown (persisted locally).
  const [visibleFilters, setVisibleFilters] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(PROMO_FILTERS_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      /* ignore */
    }
    return PROMO_DEFAULT_VISIBLE;
  });
  const [filtersMenuAnchor, setFiltersMenuAnchor] = useState<null | HTMLElement>(null);
  const showF = (key: string) => visibleFilters.includes(key);

  // Column show/hide (shared hook).
  const {
    visibleColumns,
    toggleColumn,
    headLabel: promoHeadLabel,
  } = useColumnVisibility(PROMO_COLUMNS, PROMO_COLUMNS_STORAGE_KEY);

  const { currentTab: filterStatus, onChangeTab: onChangeFilterStatus } = useTabs('all');
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Diplomas (with batches) to resolve a code's scope to readable names.
  const [diplomas, setDiplomas] = useState<any[]>([]);
  const diplomaMap: Record<string, string> = {};
  const batchMap: Record<string, string> = {};
  diplomas.forEach((d: any) => {
    if (d?._id) diplomaMap[d._id] = d.title || d.name || 'Program';
    (d?.batches ?? []).forEach((b: any) => {
      if (b?._id) batchMap[b._id] = b.name || 'Batch';
    });
  });

  useEffect(() => {
    getPromoCodeList().catch(console.error);
    (async () => {
      const res = await axiosInstance.get(APIS.DIPLOMAS.LIST);
      const d: any = res.data?.data ?? res.data;
      setDiplomas(Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : []);
    })().catch(() => setDiplomas([]));
  }, []);
  const getPromoCodeList = async () => {
    const data = await axiosInstance.get(APIS.PROMO_CODE.LIST);
    setTableData(data.data.data);
  };

  // Hiding a filter resets its value so it's never silently applied.
  const resetPromoFilter = (key: string) => {
    if (key === 'source') setFilterSource('all');
    if (key === 'status') setStatusFilter('all');
    if (key === 'partner') setPartnerFilter('');
  };
  const togglePromoFilter = (key: string) => {
    const isHiding = visibleFilters.includes(key);
    setVisibleFilters((prev) => {
      const next = isHiding ? prev.filter((k) => k !== key) : [...prev, key];
      try {
        localStorage.setItem(PROMO_FILTERS_STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
    if (isHiding) {
      resetPromoFilter(key);
      setPage(0);
    }
  };
  const clearPromoFilters = () => {
    setFilterSource('all');
    setStatusFilter('all');
    setPartnerFilter('');
    setPage(0);
  };

  // Phase 7.21: the delete confirmAction was a no-op. Wire it to the BE
  // (DELETE /v1/promoCodes/:id, @Auth(ADMIN)), then close + refetch + toast.
  const handleDeletePromo = async () => {
    if (!currentPromoCode?._id) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(
        APIS.PROMO_CODE.DELETE(currentPromoCode._id as string),
      );
      enqueueSnackbar('Promo code deleted');
      setDeleteOpen(false);
      setCurrentPromoCode(undefined);
      await getPromoCodeList();
    } catch (err: any) {
      enqueueSnackbar(
        err?.message ||
          (typeof err === 'string' ? err : 'Failed to delete promo code'),
        { variant: 'error' },
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
  };

  const handleFilterRole = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterRole(event.target.value);
  };

  const handleDeleteRows = (selected: string[]) => {
    const deleteRows = tableData?.filter((row) => !selected.includes(row._id as string));
    setSelected([]);
    setTableData(deleteRows);
  };

  const handleEditRow = (selectedPromoCode: PromoCodeType) => {
    setCurrentPromoCode(selectedPromoCode);
    setModalOpen(true);
  };

  const dataFiltered = applySortFilter({
    tableData,
    comparator: getComparator(order, orderBy),
    filterName,
    filterSource,
    statusFilter,
    partnerFilter,
  });

  const isNotFound =
    (!dataFiltered.length && !!filterName) ||
    (!dataFiltered.length && filterSource !== 'all') ||
    (!dataFiltered.length && statusFilter !== 'all') ||
    (!dataFiltered.length && !!partnerFilter);

  return (
    <Page title="Promo: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Promo List"
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'PromoCode', href: PATH_DASHBOARD.promoCode },
          ]}
          action={
            <Button
              variant="contained"
              onClick={() => setModalOpen(true)}
              startIcon={<Iconify icon={'eva:plus-fill'} />}
            >
              New PromoCode
            </Button>
          }
        />

        <Card>
          <Divider />
          <UserTableToolbar
            placeholder="Search PromoCode..."
            filterName={filterName}
            filterRole={filterRole}
            onFilterName={handleFilterName}
            onFilterRole={handleFilterRole}
            optionsRole={Sort_Options}
          />

          {/* #3: customizable filters (Students-style) — Source / Status /
              Partner name. The Filters menu picks which controls show. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1.5 }}
          >
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={(e) => setFiltersMenuAnchor(e.currentTarget)}
              startIcon={<Iconify icon="eva:options-2-outline" />}
            >
              Filters
            </Button>
            <Menu
              anchorEl={filtersMenuAnchor}
              open={Boolean(filtersMenuAnchor)}
              onClose={() => setFiltersMenuAnchor(null)}
            >
              {PROMO_FILTER_DEFS.map((f) => (
                <MenuItem key={f.key} onClick={() => togglePromoFilter(f.key)} dense>
                  <Checkbox checked={showF(f.key)} size="small" sx={{ p: 0.5, mr: 1 }} />
                  <ListItemText primary={f.label} />
                </MenuItem>
              ))}
            </Menu>

            <ColumnsButton
              columns={PROMO_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />

            {showF('source') && (
              <TextField
                select
                size="small"
                label="Source"
                value={filterSource}
                onChange={(e) => {
                  setFilterSource(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="all">Any source</MenuItem>
                {PROMO_SOURCES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {showF('status') && (
              <TextField
                select
                size="small"
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 150 }}
              >
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {showF('partner') && (
              <TextField
                size="small"
                label="Partner name"
                value={partnerFilter}
                onChange={(e) => {
                  setPartnerFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 180 }}
              />
            )}
            <Button
              size="small"
              color="inherit"
              onClick={clearPromoFilters}
              startIcon={<Iconify icon="eva:close-fill" />}
            >
              Clear
            </Button>
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
              {selected.length > 0 && isSuperAdmin && (
                <TableSelectedActions
                  dense={dense}
                  numSelected={selected.length}
                  rowCount={tableData?.length}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData?.map((row) => row._id as string)
                    )
                  }
                  actions={
                    <Tooltip title="Delete">
                      <IconButton color="primary" onClick={() => handleDeleteRows(selected)}>
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}

              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={[...promoHeadLabel, { id: '' }]}
                  rowCount={tableData?.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      tableData?.map((row) => row._id as string)
                    )
                  }
                />

                <TableBody>
                  {dataFiltered.length > 0 &&
                    dataFiltered
                      ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      ?.map((row) => (
                        <PromoCodeTableRow
                          key={row._id}
                          row={row}
                          visibleColumns={visibleColumns}
                          canDelete={isSuperAdmin}
                          diplomaMap={diplomaMap}
                          batchMap={batchMap}
                          selected={selected.includes(row._id as string)}
                          onSelectRow={() => onSelectRow(row._id as string)}
                          onDeleteRow={() => {
                            setDeleteOpen(true);
                            setCurrentPromoCode(row);
                          }}
                          onEditRow={() => handleEditRow(row)}
                        />
                      ))}

                  <TableEmptyRows
                    height={2.2}
                    emptyRows={emptyRows(page, rowsPerPage, tableData?.length)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>
      </Container>
      <PromoModal
        isEdit={currentPromoCode ? true : false}
        currentPromoCode={currentPromoCode}
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          getPromoCodeList();
          setCurrentPromoCode(undefined);
        }}
      />
      <ConfirmationDialog
        confirmAction={handleDeletePromo}
        isOpen={deleteOpen}
        text={'Are You Sure You want To Delete This Promo'}
        headText={'Delete Promo Code'}
        onClose={() => setDeleteOpen(false)}
        loading={deleting}
      />
    </Page>
  );
}

// ----------------------------------------------------------------------

// Derived status, mirroring the table row: an expired code (past its expiry)
// reads as 'expired' regardless of the isActive flag; otherwise active/inactive.
function promoStatusOf(item: Record<string, any>): 'active' | 'inactive' | 'expired' {
  const exp = item.expiryDate ? new Date(item.expiryDate).getTime() : 0;
  if (exp && exp <= Date.now()) return 'expired';
  return item.isActive ? 'active' : 'inactive';
}

function applySortFilter({
  tableData,
  comparator,
  filterName,
  filterSource,
  statusFilter,
  partnerFilter,
}: {
  tableData: PromoCodeType[];
  comparator: (a: any, b: any) => number;
  filterName: string;
  filterSource: string;
  statusFilter: string;
  partnerFilter: string;
}) {
  const stabilizedThis = tableData?.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData?.filter(
      (item: Record<string, any>) =>
        item.code.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  if (filterSource !== 'all') {
    tableData = tableData?.filter(
      (item: Record<string, any>) => (item.source || 'admin') === filterSource
    );
  }

  if (statusFilter !== 'all') {
    tableData = tableData?.filter(
      (item: Record<string, any>) => promoStatusOf(item) === statusFilter
    );
  }

  if (partnerFilter) {
    tableData = tableData?.filter((item: Record<string, any>) =>
      (item.partnerName || '').toLowerCase().includes(partnerFilter.toLowerCase())
    );
  }

  return tableData;
}
